import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Global, css } from '@emotion/core'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Select } from 'antd'
import { Alert, Flex, Icon, InputLabel, Table } from 'components/patterns'
import { getLocalTime } from 'utils/utils'
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  VEHICLE_SUBTYPES,
} from 'utils/constants'
import { resolveDashDefinition } from 'components/SwitchAndGauge/definitions/resolveDashDefinition'
import { isUnassignedLocation } from 'components/SwitchAndGauge/utils'
import { resolveOverheadSleeperDefinitions } from 'components/SwitchAndGauge/definitions/resolveOverheadSleeperDefinitions'
import {
  MSF_POCKET_LOCATIONS_MAP,
  POCKET_LABELS,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import { MAX_SNAPSHOTS_PER_VEHICLE } from 'selectors/vehicleSnapshots'
import { ORDERED_DOMAIN_IDS } from 'constants/powerTakeOffConstants'
import { MAX_SOLENOIDS } from 'constants/ecp'
import strings from 'utils/strings'
import QPMIcon from 'img/icon_qpm.svg'
import EmptyModulesImg from 'img/empty-modules.svg'
import palette from 'theme/color/defaults'
import {
  FeatureTable,
  FeatureTableHead,
  FeatureTableBody,
  FeatureRow,
  FeatureCell,
  EmptyState,
  SnapshotSelectorTab,
  SnapshotHeader,
  SectionTabRow,
  SectionTab,
  SectionTabGroup,
  TabContentArea,
  PtoDropdownRow,
  QpmSectionHeader,
  EmptyStateBox,
  TableWrapper,
  EcpHeader,
  StyledPneumaticsTable,
  FullPageOverlay,
  ModalPageContent,
  StyledSgChecTable,
  SgListHeader,
  SgSectionHeader,
  AsBuiltOriginBanner,
} from './VehicleSnapshotModal.style'
import { SPECCED, AUTOMATED } from 'constants/featureOriginTypes'

const BASE_SECTION_TABS = ['Features', 'PTO', 'Pneumatics', 'Switch & Gauge']

const CELL_TRUNCATE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const PTO_COLUMNS = [
  {
    title: strings.settings,
    dataIndex: 'name',
    width: 465,
    onCell: () => ({ style: CELL_TRUNCATE }),
    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
  },
  {
    title: strings.value,
    dataIndex: 'value',
    width: 90,
    render: (val) => formatPtoValue(val),
    onCell: () => ({ style: CELL_TRUNCATE }),
    sorter: (a, b) =>
      formatPtoValue(a.value || '').localeCompare(
        formatPtoValue(b.value || '')
      ),
  },
  {
    title: strings.unit,
    dataIndex: 'units',
    width: 90,
    onCell: () => ({ style: CELL_TRUNCATE }),
    sorter: (a, b) => (a.units || '').localeCompare(b.units || ''),
  },
  {
    title: strings.module,
    dataIndex: 'moduleNo',
    width: 90,
    onCell: () => ({ style: CELL_TRUNCATE }),
    sorter: (a, b) => (a.moduleNo || '').localeCompare(b.moduleNo || ''),
  },
  {
    title: strings.refNo,
    dataIndex: 'refNo',
    width: 90,
    sorter: (a, b) => {
      const [prefA] = (a.refNo || '').split('_')
      const [prefB] = (b.refNo || '').split('_')
      const nA = parseInt(prefA, 10)
      const nB = parseInt(prefB, 10)
      if (!Number.isNaN(nA) && !Number.isNaN(nB)) return nA - nB
      return (a.refNo || '').localeCompare(b.refNo || '')
    },
    onCell: () => ({ style: CELL_TRUNCATE }),
  },
  {
    title: strings.description,
    dataIndex: 'description',
    onCell: () => ({ style: CELL_TRUNCATE }),
    sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
  },
]

const PtoStyledTable = styled(Table)`
  &&&& {
    table {
      table-layout: fixed !important;
    }
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      height: 35px !important;
    }
    .ant-table-tbody > tr:hover > td {
      background: transparent !important;
    }
  }
`

const SORT_ACTIVE_COLOR = palette.blueSecondary
const SORT_INACTIVE_COLOR = 'rgba(7, 7, 15, 0.25)'
const FEATURE_LINK_COLOR = palette.blueSecondary

const SortIndicator = ({ sortState, sortKey }) => (
  <span
    aria-hidden="true"
    style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0 }}
  >
    <Icon
      icon="FaCaretUp"
      size={10}
      color={
        sortState.key === sortKey && sortState.dir === 'asc'
          ? SORT_ACTIVE_COLOR
          : SORT_INACTIVE_COLOR
      }
    />
    <Icon
      icon="FaCaretDown"
      size={10}
      color={
        sortState.key === sortKey && sortState.dir === 'desc'
          ? SORT_ACTIVE_COLOR
          : SORT_INACTIVE_COLOR
      }
    />
  </span>
)

SortIndicator.propTypes = {
  sortState: PropTypes.shape({
    key: PropTypes.string,
    dir: PropTypes.string,
  }).isRequired,
  sortKey: PropTypes.string.isRequired,
}

const isXmc3Plus = (moduleName) => {
  if (!moduleName || !moduleName.startsWith('XMC')) return false
  const position = parseInt(moduleName.slice(3), 10)
  return !Number.isNaN(position) && position >= 3
}

const MAX_SNAPSHOT_SLOTS = MAX_SNAPSHOTS_PER_VEHICLE

const sortDomainsBySpec = (domains) => {
  const sorted = [...domains].sort((a, b) => {
    const posA = ORDERED_DOMAIN_IDS.indexOf(a.id)
    const posB = ORDERED_DOMAIN_IDS.indexOf(b.id)
    if (posA === -1 && posB === -1) return 0
    if (posA === -1) return 1
    if (posB === -1) return -1
    return posA - posB
  })
  return sorted
}

const formatPtoValue = (value) =>
  value === 'Function available' ? 'On' : value

const PtoTabContent = ({ pto, selectedDomainId, onDomainChange }) => {
  const domains = (pto && pto.domains) || []
  if (domains.length === 0) {
    return (
      <EmptyStateBox>
        <img src={EmptyModulesImg} alt="" width={170} height={120} />
        <EmptyState>{strings.snapshot_pto_no_data}</EmptyState>
      </EmptyStateBox>
    )
  }

  const sortedDomains = sortDomainsBySpec(domains)
  const activeDomainId = selectedDomainId || sortedDomains[0].id
  const activeDomain =
    sortedDomains.find((d) => d.id === activeDomainId) || sortedDomains[0]
  const settings = activeDomain.settings || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <PtoDropdownRow>
        <InputLabel
          label={strings.snapshot_pto_domain_label}
          htmlFor="pto-domain-select"
        />
        <Select
          id="pto-domain-select"
          data-testid="pto-domain-select"
          value={activeDomainId}
          onChange={onDomainChange}
          style={{ width: '100%' }}
          getPopupContainer={(trigger) => trigger.parentElement}
        >
          {sortedDomains.map((d) => (
            <Select.Option key={d.id} value={d.id}>
              {d.name}
            </Select.Option>
          ))}
        </Select>
      </PtoDropdownRow>

      <PtoStyledTable
        columns={PTO_COLUMNS}
        dataSource={settings.map((s, i) => ({ key: s.id || i, ...s }))}
        pagination={false}
        noResultText={{ emptyText: strings.snapshot_pto_no_settings }}
      />
    </div>
  )
}

PtoTabContent.propTypes = {
  pto: PropTypes.shape({
    domains: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        settings: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            value: PropTypes.string,
            units: PropTypes.string,
            moduleNo: PropTypes.string,
            refNo: PropTypes.string,
            description: PropTypes.string,
          })
        ),
      })
    ),
  }),
  selectedDomainId: PropTypes.string,
  onDomainChange: PropTypes.func.isRequired,
}

PtoTabContent.defaultProps = {
  pto: null,
  selectedDomainId: null,
}

const PNEUMATICS_COLUMNS = [
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    width: '11%',
    // Solenoid locations from the API are prefixed with the ECP number (e.g. "101"
    // means ECP 1, position 1). Display only the slot position (1–6) by stripping
    // the prefix: parse as a number and take the remainder mod 100, which maps
    // 101→1, 102→2 … 106→6. Fall back to the raw string for non-numeric values.
    render: (v) => {
      if (!v) return 'N/A'
      const n = parseInt(v, 10)
      if (!Number.isNaN(n) && n > 100) return String(n % 100)
      return v
    },
  },
  {
    title: 'Function',
    dataIndex: 'function',
    key: 'function',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Air Circuit',
    dataIndex: 'circuit',
    key: 'circuit',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Collet Part No',
    dataIndex: 'colletPartNo',
    key: 'colletPartNo',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Collet Color',
    dataIndex: 'colletColor',
    key: 'colletColor',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Part No.',
    dataIndex: 'partNo',
    key: 'partNo',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Feature No.',
    dataIndex: 'featureNo',
    key: 'featureNo',
    width: '11%',
    render: (v) => v || 'N/A',
  },
  {
    title: 'Feature Description',
    dataIndex: 'featureDescription',
    key: 'featureDescription',
    width: '13%',
    render: (v) => v || 'N/A',
  },
]

/**
 * For normal snapshot views, build a fixed-slot row list with all MAX_SOLENOIDS
 * positions represented. Slots with no API data render with N/A fallbacks,
 * preserving the established six-position layout that predates the as-built work.
 *
 * For as-built views (filterAsBuilt === true), render only factory-confirmed
 * solenoids (asBuilt === true) — no fixed slots.
 */
const buildPneumaticsRows = (ecp, filterAsBuilt) => {
  const solenoids = ecp.solenoids || []
  if (filterAsBuilt) {
    return solenoids.filter((s) => s.asBuilt === true)
  }
  // Build a lookup keyed by location string for O(1) slot filling.
  const byLocation = {}
  solenoids.forEach((s) => {
    if (s.location) byLocation[s.location] = s
  })
  const rows = []
  let i = 1
  while (i <= MAX_SOLENOIDS) {
    const loc = String(i)
    const s = byLocation[loc]
    rows.push(
      s
        ? { ...s, key: `${ecp.name}-${loc}` }
        : {
            key: `${ecp.name}-slot-${loc}`,
            location: loc,
            function: null,
            circuit: null,
            colletPartNo: null,
            colletColor: null,
            partNo: null,
            featureNo: null,
            featureDescription: null,
          }
    )
    i += 1
  }
  return rows
}

const PneumaticsTabContent = ({ pneumatics, filterAsBuilt }) => {
  const ecps = (pneumatics && pneumatics.ecps) || []
  if (ecps.length === 0) {
    return (
      <EmptyStateBox>
        <img src={EmptyModulesImg} alt="" width={170} height={120} />
        <EmptyState>{strings.snapshot_pneumatics_no_data}</EmptyState>
      </EmptyStateBox>
    )
  }

  if (filterAsBuilt) {
    const allSolenoids = ecps.flatMap((ecp) => ecp.solenoids || [])
    const allNull =
      allSolenoids.length > 0 &&
      allSolenoids.every((s) => s.asBuilt === null || s.asBuilt === undefined)
    if (allNull) {
      return (
        <EmptyStateBox>
          <img src={EmptyModulesImg} alt="" width={170} height={120} />
          <EmptyState>{strings.snapshot_factory_data_unavailable}</EmptyState>
        </EmptyStateBox>
      )
    }
    // Aggregate check: if no solenoid across all ECPs has asBuilt === true
    // (covers all-false and empty-solenoid cases), show a no-data state
    // rather than rendering a blank tab. Mirrors the S&G factoryComponents check.
    const factorySolenoidCount = allSolenoids.filter(
      (s) => s.asBuilt === true
    ).length
    if (factorySolenoidCount === 0) {
      return (
        <EmptyStateBox>
          <img src={EmptyModulesImg} alt="" width={170} height={120} />
          <EmptyState>{strings.snapshot_pneumatics_no_data}</EmptyState>
        </EmptyStateBox>
      )
    }
  }

  return (
    <>
      {ecps.map((ecp) => {
        const rows = buildPneumaticsRows(ecp, filterAsBuilt)
        if (filterAsBuilt && rows.length === 0) return null
        return (
          <div key={ecp.name}>
            <EcpHeader>{ecp.name}</EcpHeader>
            <StyledPneumaticsTable
              columns={PNEUMATICS_COLUMNS}
              dataSource={rows}
              rowKey="key"
              pagination={false}
              size="small"
            />
          </div>
        )
      })}
    </>
  )
}

PneumaticsTabContent.propTypes = {
  pneumatics: PropTypes.shape({
    ecps: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        solenoids: PropTypes.arrayOf(
          PropTypes.shape({
            location: PropTypes.string,
            function: PropTypes.string,
            circuit: PropTypes.string,
            colletPartNo: PropTypes.string,
            colletColor: PropTypes.string,
            partNo: PropTypes.string,
            featureNo: PropTypes.string,
            featureDescription: PropTypes.string,
            asBuilt: PropTypes.bool,
          })
        ),
      })
    ),
  }),
  filterAsBuilt: PropTypes.bool,
}

PneumaticsTabContent.defaultProps = {
  pneumatics: null,
  filterAsBuilt: false,
}

const SG_TABLE_COLUMNS = [
  {
    title: strings.location_title,
    dataIndex: 'location',
    key: 'location',
    width: 65,
    render: (loc) => <span>{loc || strings.n_a_lowercase}</span>,
  },
  {
    title: strings.desc_title,
    dataIndex: 'panelDescription',
    key: 'panelDescription',
    width: 258,
    render: (desc) => <span>{desc || strings.n_a_lowercase}</span>,
  },
  {
    title: strings.partnumber_title,
    dataIndex: 'panelNumber',
    key: 'panelNumber',
    width: 137,
    render: (pn) => <span>{pn || strings.n_a_lowercase}</span>,
  },
  {
    title: strings.bom_title,
    dataIndex: 'bom',
    key: 'bom',
    width: 115,
    render: (bom) => <span>{bom || strings.n_a_lowercase}</span>,
  },
  {
    title: strings.cap_part_no,
    dataIndex: 'capPartNumber',
    key: 'capPartNumber',
    width: 115,
    render: (cpn) => <span>{cpn || strings.n_a_lowercase}</span>,
  },
  {
    title: strings.cap_description,
    dataIndex: 'capDescription',
    key: 'capDescription',
    width: 258,
    render: (cd) => <span>{cd || strings.n_a_lowercase}</span>,
  },
]

const normalizeComponent = (c) => ({
  location: c.location || '',
  locationDetail: c.locationDetail || '',
  locationType: c.locationType || '',
  parent: c.parent || '',
  panelNumber: c.panelNumber || c.partNumber || '',
  panelDescription: c.panelDescription || c.description || '',
  bom: c.bom || '',
  capPartNumber: c.capPartNumber || '',
  capDescription: c.capDescription || '',
  asBuilt: c.asBuilt ?? null,
})

// Re-group components using the dashboard definition, mirroring PartTableView / getDashListsDefinitions.
// For MSF/MST groups: match by component's immediate parent === group.id.
// For non-MSF groups: match by component's immediate parent === panel root id, filtered by locationDetail.
const regroupByDefinition = (flatComponents, definition) => {
  if (!definition || !definition.lists) return null

  // Build the set of structural location codes (panel IDs, group IDs, and group
  // panel IDs). Some definitions use a group.panel that differs from both the list
  // id and the group id (e.g. 43N RHD: list=AGFAC, group.panel=AGPNL). A hardware
  // component at that panel ID is still structural and must never be shown.
  const structuralLocations = new Set()
  Object.values(definition.lists).forEach((list) => {
    structuralLocations.add(list.id)
    Object.values(list.groups || {}).forEach((g) => {
      structuralLocations.add(g.id)
      if (g.panel) structuralLocations.add(g.panel)
    })
  })

  // "Parts Without Locations" holds exactly the components that have no location,
  // which is what the live S&G page shows there (Bug 508543).
  //
  // It deliberately does NOT include components that have a location but lack
  // parent/locationDetail. Treating those as unlocated turned the section into a
  // catch-all for anything the dashboard definition could not route, and the
  // backend's `switchAndGauge.unassigned` array is polluted with located, non-S&G
  // hardware. Measured on MD1587 (snapshot 86687, DEV): `unassigned` carried 57
  // entries — the 7 genuinely unassigned switches, all with an empty location, plus
  // 50 located parts that do not belong to Switch & Gauge at all:
  //   power-distribution fuses (PDF*/PD*), relays (PDR*), circuit protection (PA*),
  //   electrical-box ECUs and junction blocks (EB*), HVAC controls, fascias.
  // The old predicate surfaced all 57; the live page shows 7.
  //
  // Keying on the absence of a location is also what makes this robust: it yields
  // the correct 7 regardless of how much extra located hardware the backend puts in
  // `unassigned`. Located components that cannot be routed are dropped instead,
  // which is what the live page already does — its bucket is the explicit unassigned
  // list and anything matching no location is simply not rendered. See
  // getDashUnassignedList in selectors/dash.js and isUnassignedLocation in
  // SwitchAndGauge/utils.js.
  //
  // No structural-location guard is needed here: structural hardware is identified
  // by its location, so a component with no location can never be structural.
  // slotComponents below still applies that filter.
  const noLocation = flatComponents.filter(
    (c) => !c.location || isUnassignedLocation(c.location)
  )

  const slotComponents = flatComponents.filter(
    (c) =>
      c.location &&
      !isUnassignedLocation(c.location) &&
      c.parent &&
      (c.locationDetail || c.locationType === 'POCKET') &&
      !structuralLocations.has(c.location)
  )

  const assigned = new Set()
  const panels = []

  Object.values(definition.lists).forEach((list) => {
    const panelId = list.id
    const panelLabel = list.label
    const sections = []

    const groups = Object.values(list.groups || {}).sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    )

    groups.forEach((group) => {
      const isMsf = group.isMsfOrMst
      const details = group.detail || []

      const groupParent = group.panel || panelId
      const matching = slotComponents.filter((c) => {
        if (assigned.has(c.location)) return false
        // Pocket components: route to the MSF group whose id maps to this pocket location.
        if (c.locationType === 'POCKET') {
          return isMsf && MSF_POCKET_LOCATIONS_MAP[c.location] === group.id
        }
        if (isMsf) {
          return c.parent === group.id && details.includes(c.locationDetail)
        }
        return c.parent === groupParent && details.includes(c.locationDetail)
      })

      if (matching.length > 0) {
        matching.forEach((c) => assigned.add(c.location))
        matching.sort((a, b) =>
          a.location.localeCompare(b.location, undefined, { numeric: true })
        )
        // If all matched components are pockets, use the pocket label; otherwise use the group label.
        const allPockets = matching.every((c) => c.locationType === 'POCKET')
        const label = allPockets
          ? POCKET_LABELS[matching[0].location] || group.label
          : group.label
        sections.push({ label, components: matching })
      }
    })

    if (sections.length > 0) {
      panels.push({ panelId, panelLabel, sections })
    }
  })

  return { panels, noLocation }
}

const normalizePanel = (panel) => {
  const panelId = panel.panelId || panel.panelDescription || ''
  const sections = (panel.sections || []).map((s) => ({
    id: s.id || s.name || '',
    components: (s.components || []).map(normalizeComponent),
  }))
  if (sections.length === 0 && panel.components) {
    const components = panel.components.map(normalizeComponent)
    return { panelId, sections: [{ id: panelId, components }] }
  }
  return { panelId, sections }
}

const OTHER_IDENTIFIERS = ['other', 'parts without locations']

const isOtherPanel = (panel) => {
  const id = (panel.panelId || '').toLowerCase()
  if (OTHER_IDENTIFIERS.includes(id)) return true
  if (id.endsWith('_other')) return true
  const { sections } = panel
  return sections.length === 1 && isOtherSection(sections[0])
}

const isOtherSection = (section) => {
  const id = (section.id || '').toLowerCase()
  return (
    id.endsWith('_other') ||
    OTHER_IDENTIFIERS.includes(id) ||
    id === 'parts without locations'
  )
}

const findDefinitionList = (definition, panelId) => {
  if (!definition || !definition.lists) return null
  const { lists } = definition
  if (lists[panelId]) return lists[panelId]
  const match = Object.values(lists).find((list) => list.id === panelId)
  return match || null
}

const getSectionLabel = (definitionList, sectionId) => {
  if (!definitionList || !definitionList.groups) return sectionId
  const { groups } = definitionList
  if (groups[sectionId]) return groups[sectionId].label
  const byId = Object.values(groups).find((g) => g.id === sectionId)
  if (byId) return byId.label
  const matchingGroups = Object.values(groups).filter(
    (g) => g.detail && g.detail.includes(sectionId)
  )
  if (matchingGroups.length === 1) return matchingGroups[0].label
  if (matchingGroups.length > 1) {
    const nonOther = matchingGroups.filter(
      (g) => g.id !== 'other' && g.id !== 'gauges'
    )
    if (nonOther.length > 0 && nonOther.every((g) => g.isMsfOrMst)) {
      return 'Switches'
    }
    return matchingGroups[0].label
  }
  return sectionId
}

const SwitchGaugeTabContent = ({
  switchAndGauge,
  dashboardType,
  vehicleSubType,
  vehicleType,
  dataBookCodes,
  isPreProduction,
  filterAsBuilt,
}) => {
  const rawPanels = (switchAndGauge && switchAndGauge.panels) || []
  const mainDefinition = resolveDashDefinition({
    dashboardType,
    vehicleSubType,
    vehicleType,
    dataBookCodes,
    isPreProduction,
  })
  // Note: the live S&G panel contains a selector (dashListsDefinitionsDatabookCodesModifierSelector
  // in selectors/dash.js) that is intended to hide the AUPNL list for digital-dash (811-047)
  // vehicles, but the filter uses def.get('name') when list entries only carry an 'id' field —
  // making it a no-op. AUPNL is therefore visible in the live panel for digital-dash vehicles
  // today. The snapshot shows AUPNL under the same conditions, so the two views already match.
  // If the live-panel selector is ever corrected, this component should apply the same filter.

  // Build the combined definition (main + overhead + sleeper) matching the live S&G panel's
  // dashListsSelector order: overhead lists first, main-dash second, sleeper last.
  // dashListsSelector merges all lists then sorts SLEEPER type to the end, so
  // overhead → main → sleeper (e.g. OL Panel before AG Panel before Sleeper Panel).
  const overheadSleeperDefs = resolveOverheadSleeperDefinitions(
    vehicleSubType,
    dataBookCodes || []
  )
  const { overhead: overheadDef, sleeper: sleeperDef } = overheadSleeperDefs
  const definition =
    mainDefinition && (overheadDef || sleeperDef)
      ? {
          ...mainDefinition,
          lists: Object.assign(
            {},
            overheadDef ? overheadDef.lists : {},
            mainDefinition.lists,
            sleeperDef ? sleeperDef.lists : {}
          ),
        }
      : mainDefinition

  const hasData =
    rawPanels.length > 0 ||
    (switchAndGauge &&
      switchAndGauge.unassigned &&
      switchAndGauge.unassigned.length > 0)

  if (!hasData) {
    return (
      <EmptyStateBox>
        <img src={EmptyModulesImg} alt="" width={170} height={120} />
        <EmptyState>{strings.snapshot_sng_no_data}</EmptyState>
      </EmptyStateBox>
    )
  }

  // Flatten all components from backend response.
  // Use normalizePanel so both panel shapes are handled consistently:
  //   - panels with sections: panel.sections[].components[]
  //   - panels with direct components: panel.components[] (older response shape)
  const flatComponents = []
  rawPanels.forEach((panel) => {
    normalizePanel(panel).sections.forEach((section) => {
      section.components.forEach((c) => {
        flatComponents.push(c)
      })
    })
  })
  ;((switchAndGauge && switchAndGauge.unassigned) || []).forEach((c) => {
    flatComponents.push(normalizeComponent(c))
  })

  // When viewing as-built, only show factory-confirmed components.
  // If all components have asBuilt === null (factory data unavailable), show an informational message.
  if (filterAsBuilt) {
    const allNull =
      flatComponents.length > 0 &&
      flatComponents.every((c) => c.asBuilt === null || c.asBuilt === undefined)
    if (allNull) {
      return (
        <EmptyStateBox>
          <img src={EmptyModulesImg} alt="" width={170} height={120} />
          <EmptyState>{strings.snapshot_factory_data_unavailable}</EmptyState>
        </EmptyStateBox>
      )
    }
    const factoryComponents = flatComponents.filter((c) => c.asBuilt === true)
    if (factoryComponents.length === 0) {
      return (
        <EmptyStateBox>
          <img src={EmptyModulesImg} alt="" width={170} height={120} />
          <EmptyState>{strings.snapshot_sng_no_data}</EmptyState>
        </EmptyStateBox>
      )
    }
    // Replace flatComponents with the factory-only subset before grouping
    flatComponents.length = 0
    factoryComponents.forEach((c) => flatComponents.push(c))
  }

  // Use definition-based grouping if components carry parent/locationDetail (new snapshots).
  // Fall back to legacy rendering for old snapshots that predate these fields.
  const hasRoutingData = flatComponents.some(
    (c) => c.parent !== '' || c.locationDetail !== ''
  )
  const grouped =
    definition && hasRoutingData
      ? regroupByDefinition(flatComponents, definition)
      : null

  if (grouped) {
    const { panels: defPanels, noLocation } = grouped
    // Components with no location OR the UNASSIGNED_PANEL_PART sentinel.
    // Slot components that have routing fields but match no definition group are
    // simply not rendered — same as the live S&G page (Bug 508543).
    const unplacedComponents = noLocation
    return (
      <>
        {unplacedComponents.length > 0 && (
          <div>
            <SgListHeader>{strings.parts_without_locations}</SgListHeader>
            <StyledSgChecTable
              columns={SG_TABLE_COLUMNS}
              dataSource={unplacedComponents.map((c, i) => ({
                key: `no-loc-${i}`,
                ...c,
              }))}
              rowKey="key"
              pagination={false}
              size="small"
            />
          </div>
        )}
        {defPanels.map((panel) =>
          panel.sections.map((section) => {
            const sectionComponents = section.components.map((c, i) => ({
              key: `${panel.panelId}-${section.label}-${i}`,
              ...c,
            }))
            return (
              <div key={`${panel.panelId}-${section.label}`}>
                {section === panel.sections[0] && (
                  <SgListHeader>{panel.panelLabel}</SgListHeader>
                )}
                <div>
                  <SgSectionHeader>{section.label}</SgSectionHeader>
                  <StyledSgChecTable
                    columns={SG_TABLE_COLUMNS}
                    dataSource={sectionComponents}
                    rowKey="key"
                    pagination={false}
                    size="small"
                  />
                </div>
              </div>
            )
          })
        )}
      </>
    )
  }

  // Legacy rendering for old snapshots without parent/locationDetail
  const normalizedPanels = rawPanels.map(normalizePanel)
  const realPanels = []
  const partsWithoutLocation = []
  const otherParts = []

  normalizedPanels.forEach((panel) => {
    if (isOtherPanel(panel)) {
      const components = panel.sections.flatMap((s) => s.components || [])
      components.forEach((c) => {
        // When viewing as-built, only show factory-confirmed components —
        // same guarantee as the definition-based path above.
        if (filterAsBuilt && c.asBuilt !== true) return
        if (!c.location) {
          partsWithoutLocation.push(c)
        } else {
          otherParts.push(c)
        }
      })
    } else {
      // Filter each section's components to factory-only when in as-built mode.
      // Drop sections and panels that become empty so the UI stays clean.
      if (filterAsBuilt) {
        const filteredSections = panel.sections
          .map((s) => ({
            ...s,
            components: (s.components || []).filter((c) => c.asBuilt === true),
          }))
          .filter((s) => s.components.length > 0)
        if (filteredSections.length > 0) {
          realPanels.push({ ...panel, sections: filteredSections })
        }
      } else {
        realPanels.push(panel)
      }
    }
  })

  // Unassigned components belong in "Parts without locations" — but only those that
  // genuinely have no location. The backend pads switchAndGauge.unassigned with
  // located non-S&G hardware (fuses, relays, junction blocks), so the same
  // empty-location predicate the definition path uses applies here too, otherwise
  // Bug 508543 survives on the legacy/fallback path.
  // When viewing as-built, also apply the factory-only filter here.
  ;((switchAndGauge && switchAndGauge.unassigned) || [])
    .map(normalizeComponent)
    .filter((c) => !c.location || isUnassignedLocation(c.location))
    .filter((c) => !filterAsBuilt || c.asBuilt === true)
    .forEach((c) => {
      partsWithoutLocation.push(c)
    })

  return (
    <>
      {partsWithoutLocation.length > 0 && (
        <div>
          <SgListHeader>{strings.parts_without_locations}</SgListHeader>
          <StyledSgChecTable
            columns={SG_TABLE_COLUMNS}
            dataSource={partsWithoutLocation.map((c, i) => ({
              key: `no-loc-${i}`,
              ...c,
            }))}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </div>
      )}

      {realPanels
        .slice()
        .sort((a, b) => {
          if (!definition || !definition.lists) return 0
          const defListIds = Object.values(definition.lists).map((l) => l.id)
          const aIdx = defListIds.indexOf(a.panelId)
          const bIdx = defListIds.indexOf(b.panelId)
          const aOrder = aIdx >= 0 ? aIdx : defListIds.length
          const bOrder = bIdx >= 0 ? bIdx : defListIds.length
          return aOrder - bOrder
        })
        .map((panel) => {
          const defList = findDefinitionList(definition, panel.panelId)
          const panelLabel = (defList && defList.label) || panel.panelId

          return (
            <div key={panel.panelId}>
              <SgListHeader>{panelLabel}</SgListHeader>
              {(panel.sections || []).map((section) => {
                const sectionComponents = (section.components || [])
                  .slice()
                  .sort((a, b) =>
                    (a.location || '').localeCompare(
                      b.location || '',
                      undefined,
                      { numeric: true }
                    )
                  )
                  .map((c, i) => ({
                    key: `${panel.panelId}-${section.id}-${i}`,
                    ...c,
                  }))
                const sectionLabel = getSectionLabel(defList, section.id)
                const sectionTitle = (
                  <SgSectionHeader>{sectionLabel}</SgSectionHeader>
                )
                return (
                  <div key={section.id}>
                    {sectionTitle}
                    <StyledSgChecTable
                      columns={SG_TABLE_COLUMNS}
                      dataSource={sectionComponents}
                      rowKey="key"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )
              })}
            </div>
          )
        })}

      {otherParts.length > 0 && (
        <div>
          <SgListHeader>Other</SgListHeader>
          <StyledSgChecTable
            columns={SG_TABLE_COLUMNS}
            dataSource={otherParts.map((c, i) => ({
              key: `other-${i}`,
              ...c,
            }))}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </div>
      )}
    </>
  )
}

const sgComponentShape = PropTypes.shape({
  location: PropTypes.string,
  panelNumber: PropTypes.string,
  panelDescription: PropTypes.string,
  bom: PropTypes.string,
  capPartNumber: PropTypes.string,
  capDescription: PropTypes.string,
  asBuilt: PropTypes.bool,
})

SwitchGaugeTabContent.propTypes = {
  switchAndGauge: PropTypes.shape({
    panels: PropTypes.arrayOf(
      PropTypes.shape({
        panelId: PropTypes.string,
        sections: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(sgComponentShape),
          })
        ),
      })
    ),
    unassigned: PropTypes.arrayOf(sgComponentShape),
  }),
  dashboardType: PropTypes.string,
  vehicleSubType: PropTypes.string,
  vehicleType: PropTypes.string,
  dataBookCodes: PropTypes.arrayOf(PropTypes.string),
  isPreProduction: PropTypes.bool,
  filterAsBuilt: PropTypes.bool,
}

SwitchGaugeTabContent.defaultProps = {
  switchAndGauge: null,
  dashboardType: null,
  vehicleSubType: null,
  vehicleType: null,
  dataBookCodes: null,
  isPreProduction: false,
  filterAsBuilt: false,
}

const VehicleSnapshotModal = ({
  snapshots,
  serialNumber,
  initialSnapshotId,
  initialSection,
  asBuiltData,
  onClose,
}) => {
  const sorted = [...snapshots].sort((a, b) => {
    if (!a.createdAt) return 1
    if (!b.createdAt) return -1
    return new Date(a.createdAt) - new Date(b.createdAt)
  })

  const initialIndex = initialSnapshotId
    ? Math.max(
        sorted.findIndex((s) => s.id === initialSnapshotId),
        0
      )
    : 0

  const showAsBuiltTab =
    asBuiltData &&
    (asBuiltData.buildDate || asBuiltData.loading || asBuiltData.error)

  const sectionTabs = BASE_SECTION_TABS

  const [selectedIndex, setSelectedIndex] = useState(initialIndex)
  const [viewingAsBuilt, setViewingAsBuilt] = useState(
    initialSection === 'As-Built'
  )
  const [activeSection, setActiveSection] = useState(
    initialSection === 'As-Built' ? 'Features' : initialSection || 'Features'
  )
  const [selectedPtoDomainId, setSelectedPtoDomainId] = useState(null)
  const [featureSort, setFeatureSort] = useState({ key: null, dir: 'asc' })
  const [asBuiltSort, setAsBuiltSort] = useState({ key: null, dir: 'asc' })

  useEffect(() => {
    setSelectedPtoDomainId(null)
    setFeatureSort({ key: null, dir: 'asc' })
  }, [selectedIndex])

  const subType = useSelector((state) =>
    state.getIn(['truck', 'vehicles', serialNumber, 'subType'])
  )
  const isSchoolBus = subType === VEHICLE_SUBTYPES.BUS

  const snapshot = sorted[selectedIndex] || sorted[0]

  const features = (snapshot ? snapshot.features || [] : []).filter((f) =>
    isXmc3Plus(f.moduleName)
  )

  const snapshotLogicSetTotal = features.reduce(
    (sum, f) => sum + (f.logicSetCount || 0),
    0
  )
  const snapshotTimerTotal = features.reduce(
    (sum, f) => sum + (f.timerCount || 0),
    0
  )
  const snapshotCanInputTotal = features.reduce(
    (sum, f) => sum + (f.canInputCount || 0),
    0
  )
  const snapshotSwitchIndicatorTotal = features.reduce(
    (sum, f) => sum + (f.switchIndicatorCount || 0),
    0
  )

  const handleFeatureSort = (key) => {
    setFeatureSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    )
  }

  const sortedFeatures = useMemo(() => {
    if (!featureSort.key) return features
    return [...features].sort((a, b) => {
      const av = (a[featureSort.key] || '').toString().toLowerCase()
      const bv = (b[featureSort.key] || '').toString().toLowerCase()
      const cmp = av.localeCompare(bv)
      return featureSort.dir === 'asc' ? cmp : -cmp
    })
  }, [features, featureSort])

  const handleAsBuiltSort = (key) => {
    setAsBuiltSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    )
  }

  const asBuiltFeatures = useMemo(() => {
    if (!asBuiltData || !asBuiltData.features) return []
    // ASAM, XMC1, and XMC2 are factory-defined content, not QuickFit Programmable Modules.
    // Filter to XMC3+ only, mirroring the snapshot path and the backend snapshot capture logic.
    // Also filter to factory-confirmed items only (asBuilt === true).
    const filtered = asBuiltData.features.filter(
      (f) => isXmc3Plus(f.moduleName) && f.asBuilt === true
    )
    if (!asBuiltSort.key) return filtered
    return [...filtered].sort((a, b) => {
      const av = (a[asBuiltSort.key] || '').toString().toLowerCase()
      const bv = (b[asBuiltSort.key] || '').toString().toLowerCase()
      const cmp = av.localeCompare(bv)
      return asBuiltSort.dir === 'asc' ? cmp : -cmp
    })
  }, [asBuiltData, asBuiltSort])

  const asBuiltLogicSetTotal = asBuiltFeatures.reduce(
    (sum, f) => sum + (f.logicSetCount || 0),
    0
  )

  const asBuiltOriginType = useMemo(() => {
    if (!asBuiltFeatures.length) return null
    const types = new Set(asBuiltFeatures.map((f) => f.originType))
    if (types.size !== 1) return null
    const [type] = [...types]
    return type || null
  }, [asBuiltFeatures])

  const ORIGIN_BANNER_MAP = {
    [SPECCED]: strings.origin_specced_banner,
    [AUTOMATED]: strings.origin_automated_banner,
  }
  const asBuiltTimerTotal = asBuiltFeatures.reduce(
    (sum, f) => sum + (f.timerCount || 0),
    0
  )
  const asBuiltCanInputTotal = asBuiltFeatures.reduce(
    (sum, f) => sum + (f.canInputCount || 0),
    0
  )
  const asBuiltSwitchIndicatorTotal = asBuiltFeatures.reduce(
    (sum, f) => sum + (f.switchIndicatorCount || 0),
    0
  )

  if (!snapshot && !viewingAsBuilt) return null

  const snapshotLabel = viewingAsBuilt
    ? asBuiltData && asBuiltData.buildDate
      ? `As-Built Record: ${getLocalTime(asBuiltData.buildDate, DATE_FORMAT)}`
      : 'As-Built Record'
    : snapshot
    ? `Snapshot ${selectedIndex + 1}: ${getLocalTime(
        snapshot.createdAt,
        DATE_TIME_FORMAT
      )}`
    : ''

  const slots = Array.from({ length: MAX_SNAPSHOT_SLOTS }, (_, i) => ({
    index: i,
    snapshot: sorted[i] || null,
  }))

  return createPortal(
    <FullPageOverlay data-testid="snapshot-modal">
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
        `}
      />
      {/* Info banner */}
      <Alert
        type="info"
        persistent
        message={
          <span>
            {strings.snapshot_viewing_vehicle} <strong>{serialNumber}</strong>
          </span>
        }
        actionTrigger={{
          text: strings.snapshot_exit_view,
          onClick: onClose,
          onColor: 'light',
          type: 'secondary',
          dataTestId: 'exit-snapshot-view-btn',
        }}
      />

      {/* Selected snapshot header */}
      <SnapshotHeader>{snapshotLabel}</SnapshotHeader>

      <ModalPageContent>
        {/* Section tabs (left) + snapshot selector (right) — same row */}
        <SectionTabRow>
          <SectionTabGroup>
            {sectionTabs.map((tab) => {
              const disabled = viewingAsBuilt && tab === 'PTO'
              return (
                <SectionTab
                  key={tab}
                  type="button"
                  data-testid={`section-tab-${tab
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                  active={activeSection === tab}
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled) setActiveSection(tab)
                  }}
                >
                  {tab}
                </SectionTab>
              )
            })}
          </SectionTabGroup>
          <SectionTabGroup>
            {slots.map(({ index, snapshot: slotSnapshot }) => (
              <SnapshotSelectorTab
                key={index}
                type="button"
                data-testid={`snapshot-tab-${index + 1}`}
                active={!viewingAsBuilt && selectedIndex === index}
                disabled={!slotSnapshot}
                onClick={() => {
                  if (slotSnapshot) {
                    setSelectedIndex(index)
                    setViewingAsBuilt(false)
                  }
                }}
              >
                {`Snapshot ${index + 1}`}
              </SnapshotSelectorTab>
            ))}
            {showAsBuiltTab && (
              <SnapshotSelectorTab
                type="button"
                data-testid="snapshot-tab-as-built"
                active={viewingAsBuilt}
                onClick={() => {
                  setViewingAsBuilt(true)
                  if (activeSection === 'PTO') {
                    setActiveSection('Features')
                  }
                }}
              >
                As-Built
              </SnapshotSelectorTab>
            )}
          </SectionTabGroup>
        </SectionTabRow>

        {/* Tab content */}
        <TabContentArea>
          {activeSection === 'Features' && !viewingAsBuilt && (
            <>
              {features.length === 0 ? (
                <EmptyStateBox>
                  <img src={EmptyModulesImg} alt="" width={170} height={120} />
                  <EmptyState>{strings.snapshot_no_feature_data}</EmptyState>
                </EmptyStateBox>
              ) : (
                <>
                  <QpmSectionHeader>
                    <img src={QPMIcon} alt="" width={24} height={24} />
                    {isSchoolBus
                      ? strings.snapshot_school_bus_modules
                      : strings.snapshot_quickfit_modules}
                  </QpmSectionHeader>
                  <TableWrapper>
                    <FeatureTable>
                      <FeatureTableHead>
                        <tr>
                          <th style={{ width: '10%' }}>Feature No.</th>
                          <th style={{ width: '4%' }}>Rev</th>
                          <th
                            style={{
                              width: '18%',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() => handleFeatureSort('name')}
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              Description
                              <SortIndicator
                                sortState={featureSort}
                                sortKey="name"
                              />
                            </span>
                          </th>
                          <th
                            style={{
                              width: '5%',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() => handleFeatureSort('moduleName')}
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              ECU
                              <SortIndicator
                                sortState={featureSort}
                                sortKey="moduleName"
                              />
                            </span>
                          </th>
                          <th style={{ width: '11%' }}>
                            Minimum Diagnostic Variant
                          </th>
                          <th style={{ width: '8%' }}>Master No.</th>
                          <th style={{ width: '8%' }}>Release No.</th>
                          <th style={{ width: '7%' }}>Status</th>
                          <th style={{ width: '7%' }}>
                            Logic Sets Used <sup>{snapshotLogicSetTotal}</sup>
                          </th>
                          <th style={{ width: '6%' }}>
                            Timer Used <sup>{snapshotTimerTotal}</sup>
                          </th>
                          <th style={{ width: '8%' }}>
                            CAN Inputs Used <sup>{snapshotCanInputTotal}</sup>
                          </th>
                          <th style={{ width: '8%' }}>
                            Switch Indicators Used{' '}
                            <sup>{snapshotSwitchIndicatorTotal}</sup>
                          </th>
                        </tr>
                      </FeatureTableHead>
                      <FeatureTableBody>
                        {sortedFeatures.map((feature) => (
                          <FeatureRow key={feature.id}>
                            <FeatureCell style={{ color: FEATURE_LINK_COLOR }}>
                              <div style={CELL_TRUNCATE}>
                                {feature.partNumber}
                              </div>
                            </FeatureCell>
                            <FeatureCell>{feature.revision}</FeatureCell>
                            <FeatureCell>
                              <div style={CELL_TRUNCATE}>{feature.name}</div>
                            </FeatureCell>
                            <FeatureCell>{feature.moduleName}</FeatureCell>
                            <FeatureCell>
                              {feature.minCompatibleVariant}
                            </FeatureCell>
                            <FeatureCell>{feature.masterNumber}</FeatureCell>
                            <FeatureCell>{feature.releaseNumber}</FeatureCell>
                            <FeatureCell>{feature.status}</FeatureCell>
                            <FeatureCell>{feature.logicSetCount}</FeatureCell>
                            <FeatureCell>{feature.timerCount}</FeatureCell>
                            <FeatureCell>{feature.canInputCount}</FeatureCell>
                            <FeatureCell>
                              {feature.switchIndicatorCount}
                            </FeatureCell>
                          </FeatureRow>
                        ))}
                      </FeatureTableBody>
                    </FeatureTable>
                  </TableWrapper>
                </>
              )}
            </>
          )}

          {activeSection === 'PTO' && (
            <PtoTabContent
              pto={
                viewingAsBuilt
                  ? asBuiltData?.pto || null
                  : snapshot
                  ? snapshot.pto
                  : null
              }
              selectedDomainId={selectedPtoDomainId}
              onDomainChange={setSelectedPtoDomainId}
            />
          )}

          {activeSection === 'Pneumatics' && (
            <PneumaticsTabContent
              pneumatics={
                viewingAsBuilt
                  ? asBuiltData?.pneumatics || null
                  : snapshot
                  ? snapshot.pneumatics
                  : null
              }
              filterAsBuilt={viewingAsBuilt}
            />
          )}

          {activeSection === 'Switch & Gauge' && (
            <SwitchGaugeTabContent
              switchAndGauge={
                viewingAsBuilt
                  ? asBuiltData?.switchAndGauge || null
                  : snapshot
                  ? snapshot.switchAndGauge
                  : null
              }
              dashboardType={
                viewingAsBuilt
                  ? asBuiltData?.dashboardType
                  : snapshot?.dashboardType
              }
              vehicleSubType={
                viewingAsBuilt
                  ? asBuiltData?.vehicleSubType
                  : snapshot?.vehicleSubType
              }
              vehicleType={
                viewingAsBuilt
                  ? asBuiltData?.vehicleType
                  : snapshot?.vehicleType
              }
              dataBookCodes={
                viewingAsBuilt
                  ? asBuiltData?.dataBookCodes
                  : snapshot?.dataBookCodes
              }
              isPreProduction={
                viewingAsBuilt
                  ? asBuiltData?.isPreProduction ?? false
                  : snapshot?.isPreProduction ?? false
              }
              filterAsBuilt={viewingAsBuilt}
            />
          )}

          {viewingAsBuilt && activeSection === 'Features' && (
            <>
              {asBuiltData && asBuiltData.loading ? (
                <EmptyStateBox>
                  <EmptyState>Loading as-built data...</EmptyState>
                </EmptyStateBox>
              ) : asBuiltData && asBuiltData.error ? (
                <EmptyStateBox>
                  <img src={EmptyModulesImg} alt="" width={170} height={120} />
                  <EmptyState>
                    Unable to load as-built data. Please try again later.
                  </EmptyState>
                </EmptyStateBox>
              ) : (
                <>
                  <Flex flexDirection="column" gap="8px">
                    <QpmSectionHeader>
                      <img src={QPMIcon} alt="" width={24} height={24} />
                      {isSchoolBus
                        ? strings.snapshot_school_bus_modules
                        : strings.snapshot_quickfit_modules}
                    </QpmSectionHeader>
                    {asBuiltOriginType && (
                      <AsBuiltOriginBanner>
                        {ORIGIN_BANNER_MAP[asBuiltOriginType]}
                      </AsBuiltOriginBanner>
                    )}
                  </Flex>
                  {!asBuiltData || asBuiltFeatures.length === 0 ? (
                    <EmptyStateBox>
                      <img
                        src={EmptyModulesImg}
                        alt=""
                        width={170}
                        height={120}
                      />
                      <EmptyState>
                        {strings.snapshot_no_feature_data}
                      </EmptyState>
                    </EmptyStateBox>
                  ) : (
                    <TableWrapper>
                      <FeatureTable>
                        <FeatureTableHead>
                          <tr>
                            <th style={{ width: '10%' }}>Feature No.</th>
                            <th style={{ width: '4%' }}>Rev</th>
                            <th
                              style={{ width: '18%' }}
                              aria-sort={
                                asBuiltSort.key === 'name'
                                  ? asBuiltSort.dir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <button
                                type="button"
                                aria-label="Sort by description"
                                onClick={() => handleAsBuiltSort('name')}
                                style={{
                                  all: 'unset',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                Description
                                <SortIndicator
                                  sortState={asBuiltSort}
                                  sortKey="name"
                                />
                              </button>
                            </th>
                            <th
                              style={{ width: '5%' }}
                              aria-sort={
                                asBuiltSort.key === 'moduleName'
                                  ? asBuiltSort.dir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : 'none'
                              }
                            >
                              <button
                                type="button"
                                aria-label="Sort by ECU"
                                onClick={() => handleAsBuiltSort('moduleName')}
                                style={{
                                  all: 'unset',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                ECU
                                <SortIndicator
                                  sortState={asBuiltSort}
                                  sortKey="moduleName"
                                />
                              </button>
                            </th>
                            <th style={{ width: '11%' }}>
                              Minimum Diagnostic Variant
                            </th>
                            <th style={{ width: '8%' }}>Master No.</th>
                            <th style={{ width: '8%' }}>Release No.</th>
                            <th style={{ width: '7%' }}>Status</th>
                            <th style={{ width: '7%' }}>
                              Logic Sets Used <sup>{asBuiltLogicSetTotal}</sup>
                            </th>
                            <th style={{ width: '6%' }}>
                              Timer Used <sup>{asBuiltTimerTotal}</sup>
                            </th>
                            <th style={{ width: '8%' }}>
                              CAN Inputs Used <sup>{asBuiltCanInputTotal}</sup>
                            </th>
                            <th style={{ width: '8%' }}>
                              Switch Indicators Used{' '}
                              <sup>{asBuiltSwitchIndicatorTotal}</sup>
                            </th>
                          </tr>
                        </FeatureTableHead>
                        <FeatureTableBody>
                          {asBuiltFeatures.map((feature) => (
                            <FeatureRow key={feature.id}>
                              <FeatureCell
                                style={{ color: FEATURE_LINK_COLOR }}
                              >
                                <div style={CELL_TRUNCATE}>
                                  {feature.partNumber}
                                </div>
                              </FeatureCell>
                              <FeatureCell>{feature.revision}</FeatureCell>
                              <FeatureCell>
                                <div style={CELL_TRUNCATE}>{feature.name}</div>
                              </FeatureCell>
                              <FeatureCell>{feature.moduleName}</FeatureCell>
                              <FeatureCell>
                                {feature.minCompatibleVariant}
                              </FeatureCell>
                              <FeatureCell>{feature.masterNumber}</FeatureCell>
                              <FeatureCell>{feature.releaseNumber}</FeatureCell>
                              <FeatureCell>{feature.status}</FeatureCell>
                              <FeatureCell>{feature.logicSetCount}</FeatureCell>
                              <FeatureCell>{feature.timerCount}</FeatureCell>
                              <FeatureCell>{feature.canInputCount}</FeatureCell>
                              <FeatureCell>
                                {feature.switchIndicatorCount}
                              </FeatureCell>
                            </FeatureRow>
                          ))}
                        </FeatureTableBody>
                      </FeatureTable>
                    </TableWrapper>
                  )}
                </>
              )}
            </>
          )}
        </TabContentArea>
      </ModalPageContent>
    </FullPageOverlay>,
    document.body
  )
}

VehicleSnapshotModal.defaultProps = {
  initialSnapshotId: null,
  initialSection: 'Features',
  asBuiltData: null,
  serialNumber: '',
}

VehicleSnapshotModal.propTypes = {
  snapshots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      createdBy: PropTypes.string,
      createdAt: PropTypes.string,
      expiresAt: PropTypes.string,
      // Vehicle metadata — persisted at snapshot creation time so the S&G
      // definition resolver (resolveDashDefinition) can pick the correct layout
      // for RHD, Bus/RV, CHECP, and digital-dash variants.
      // dashboardType has been in the API contract since before this PR;
      // the remaining four fields are confirmed present in the real API response
      // (the mock fixture was simply stale — it also lacked dashboardType).
      dashboardType: PropTypes.string,
      vehicleSubType: PropTypes.string,
      vehicleType: PropTypes.string,
      dataBookCodes: PropTypes.arrayOf(PropTypes.string),
      isPreProduction: PropTypes.bool,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string,
          partNumber: PropTypes.string,
          moduleName: PropTypes.string,
          status: PropTypes.string,
          masterNumber: PropTypes.string,
          revision: PropTypes.string,
          minCompatibleVariant: PropTypes.string,
          releaseNumber: PropTypes.string,
          logicSetCount: PropTypes.number,
          timerCount: PropTypes.number,
          canInputCount: PropTypes.number,
          switchIndicatorCount: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  serialNumber: PropTypes.string,
  initialSnapshotId: PropTypes.string,
  initialSection: PropTypes.string,
  asBuiltData: PropTypes.shape({
    features: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        partNumber: PropTypes.string,
        moduleName: PropTypes.string,
        status: PropTypes.string,
        masterNumber: PropTypes.string,
        revision: PropTypes.string,
        minCompatibleVariant: PropTypes.string,
        releaseNumber: PropTypes.string,
        logicSetCount: PropTypes.number,
        timerCount: PropTypes.number,
        canInputCount: PropTypes.number,
        switchIndicatorCount: PropTypes.number,
        asBuilt: PropTypes.bool,
        originType: PropTypes.oneOf([SPECCED, AUTOMATED]),
      })
    ),
    buildDate: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
    pneumatics: PropTypes.object,
    switchAndGauge: PropTypes.object,
    pto: PropTypes.object,
    dashboardType: PropTypes.string,
    vehicleSubType: PropTypes.string,
    vehicleType: PropTypes.string,
    dataBookCodes: PropTypes.arrayOf(PropTypes.string),
    isPreProduction: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
}

export default VehicleSnapshotModal
